/**
 * Issue #14 — Base de datos de tooltips financieros (tarea 4.1.3)
 *
 * Provee explicaciones en lenguaje simple de términos contables y financieros.
 * Usada por la directiva vFinancialTooltip y el glosario TermGlossary.
 */

export interface TooltipEntry {
  /** Término técnico (clave) */
  term: string
  /** Explicación corta (máx. ~100 chars) para el tooltip */
  short: string
  /** Explicación completa con ejemplo para el glosario */
  full: string
  /** Categoría del término */
  category: 'liquidez' | 'rentabilidad' | 'endeudamiento' | 'actividad' | 'general'
  /** Ejemplo numérico simple (opcional) */
  example?: string
}

export const FINANCIAL_TOOLTIPS: Record<string, TooltipEntry> = {
  // ── Liquidez ──────────────────────────────────────────────────────────────
  razon_corriente: {
    term: 'Razón Corriente',
    short: 'Cuánto puedes pagar de tus deudas a corto plazo con lo que tienes disponible.',
    full: 'Mide la capacidad de la empresa de pagar sus obligaciones de corto plazo usando sus activos corrientes. Un valor mayor a 1 indica que puedes cubrir tus deudas inmediatas.',
    category: 'liquidez',
    example: 'Si tienes $200 en activos y $100 en deudas → razón corriente = 2.0 (ideal ≥ 1.5)',
  },
  prueba_acida: {
    term: 'Prueba Ácida',
    short: 'Liquidez inmediata sin contar el inventario.',
    full: 'Similar a la razón corriente, pero excluye los inventarios porque son menos fáciles de convertir en dinero rápidamente. Mide la liquidez más estricta.',
    category: 'liquidez',
    example: '(Activos corrientes - Inventarios) / Pasivos corrientes',
  },
  capital_trabajo: {
    term: 'Capital de Trabajo',
    short: 'Dinero que te queda para operar después de pagar las deudas a corto plazo.',
    full: 'Es la diferencia entre activos corrientes y pasivos corrientes. Representa el "colchón" financiero con que cuenta la empresa para su operación diaria.',
    category: 'liquidez',
    example: 'Activos corrientes $500M - Pasivos corrientes $200M = Capital de trabajo $300M',
  },

  // ── Rentabilidad ──────────────────────────────────────────────────────────
  margen_neto: {
    term: 'Margen Neto',
    short: 'De cada peso de ventas, cuánto queda como ganancia final.',
    full: 'Es la utilidad neta dividida por los ingresos totales. Indica qué porcentaje de las ventas se convierte en ganancia después de todos los gastos e impuestos.',
    category: 'rentabilidad',
    example: 'Si vendiste $100M y ganaste $10M → Margen neto = 10%',
  },
  margen_bruto: {
    term: 'Margen Bruto',
    short: 'Ganancia de las ventas antes de descontar gastos administrativos.',
    full: 'Es la diferencia entre ventas e ingresos directamente relacionados con la producción, dividida por las ventas. Muestra cuánto queda después de pagar los costos de producción.',
    category: 'rentabilidad',
    example: 'Ventas $100M - Costo de ventas $70M = Margen bruto $30M (30%)',
  },
  roa: {
    term: 'ROA (Rentabilidad del Activo)',
    short: 'Qué tan eficientemente usas tus activos para generar ganancia.',
    full: 'Return on Assets: mide la ganancia generada por cada peso invertido en activos. Ayuda a comparar la eficiencia operativa entre empresas del mismo sector.',
    category: 'rentabilidad',
    example: 'Utilidad $20M / Total activos $200M = ROA 10%',
  },
  roe: {
    term: 'ROE (Rentabilidad del Patrimonio)',
    short: 'Cuánto gana la empresa por cada peso que los dueños tienen invertido.',
    full: 'Return on Equity: mide la ganancia generada por cada peso de capital propio. Es el indicador más importante para los dueños o accionistas.',
    category: 'rentabilidad',
    example: 'Utilidad $20M / Patrimonio $100M = ROE 20%',
  },

  // ── Endeudamiento ─────────────────────────────────────────────────────────
  nivel_endeudamiento: {
    term: 'Nivel de Endeudamiento',
    short: 'Qué porcentaje de tus activos están financiados con deuda.',
    full: 'Mide el porcentaje de los activos que fueron financiados con deudas. Un nivel alto implica mayor riesgo financiero.',
    category: 'endeudamiento',
    example: 'Pasivos $400M / Activos $1.000M = 40% de endeudamiento',
  },
  leverage: {
    term: 'Apalancamiento (Leverage)',
    short: 'Cuántas veces la deuda supera al patrimonio de la empresa.',
    full: 'Relación entre la deuda total y el patrimonio. Un valor alto significa que la empresa depende más de deuda externa que de recursos propios para financiarse.',
    category: 'endeudamiento',
    example: 'Deuda $300M / Patrimonio $100M = Leverage 3x',
  },
  cobertura_intereses: {
    term: 'Cobertura de Intereses',
    short: 'Cuántas veces puedes pagar tus intereses con tus ganancias operativas.',
    full: 'Mide la capacidad de la empresa para pagar los intereses de su deuda usando la utilidad operacional. Un valor menor a 1 indica riesgo de incumplimiento.',
    category: 'endeudamiento',
    example: 'EBIT $60M / Intereses $20M = Cobertura 3x (muy buena)',
  },

  // ── Actividad ─────────────────────────────────────────────────────────────
  rotacion_inventarios: {
    term: 'Rotación de Inventarios',
    short: 'Cuántas veces al año vendiste todo tu inventario.',
    full: 'Número de veces que el inventario se convierte en ventas durante el año. Un número alto indica que vendes rápido; un número bajo puede señalar producto acumulado.',
    category: 'actividad',
    example: 'Costo de ventas $700M / Inventario promedio $100M = 7 veces al año',
  },
  rotacion_cartera: {
    term: 'Rotación de Cartera',
    short: 'Cuántas veces al año cobras la totalidad de lo que te deben tus clientes.',
    full: 'Mide qué tan rápido cobras las cuentas por cobrar. Un número alto es bueno: indica que cobras rápido.',
    category: 'actividad',
    example: 'Ventas $1.200M / Cartera promedio $200M = 6 veces al año (cobras cada 2 meses)',
  },
  dias_cobro: {
    term: 'Días de Cobro',
    short: 'En promedio, cuántos días tardas en cobrar a tus clientes.',
    full: 'Indica el número promedio de días que tarda la empresa en cobrar sus ventas a crédito. Menos días = mejor gestión de cobros.',
    category: 'actividad',
    example: '365 / Rotación cartera 6 = 61 días en promedio',
  },

  // ── General ───────────────────────────────────────────────────────────────
  activos_corrientes: {
    term: 'Activos Corrientes',
    short: 'Todo lo que la empresa posee y puede convertir en dinero en menos de un año.',
    full: 'Incluye caja, bancos, inventarios, cuentas por cobrar y cualquier inversión de corto plazo. Son los recursos más líquidos de la empresa.',
    category: 'general',
  },
  pasivos_corrientes: {
    term: 'Pasivos Corrientes',
    short: 'Todas las deudas que debes pagar en menos de un año.',
    full: 'Incluye préstamos bancarios de corto plazo, cuentas por pagar a proveedores, impuestos por pagar y cualquier otra obligación de vencimiento inmediato.',
    category: 'general',
  },
  patrimonio: {
    term: 'Patrimonio',
    short: 'El valor real de la empresa que pertenece a sus dueños.',
    full: 'Es la diferencia entre los activos totales y los pasivos totales. Representa la participación de los propietarios en los recursos de la empresa.',
    category: 'general',
    example: 'Activos $500M - Pasivos $300M = Patrimonio $200M',
  },
  ebitda: {
    term: 'EBITDA',
    short: 'Ganancia operativa antes de descontar impuestos, deuda y depreciaciones.',
    full: 'Earnings Before Interest, Taxes, Depreciation and Amortization. Es una medida de la rentabilidad operativa pura de la empresa, sin distorsiones contables o financieras.',
    category: 'rentabilidad',
    example: 'Muy usado para comparar empresas del mismo sector sin importar su estructura de deuda',
  },
}

/**
 * Composable para acceder a la base de tooltips.
 * Útil en componentes Vue.
 */
export function useFinancialTooltips() {
  function getTooltip(key: string): TooltipEntry | undefined {
    return FINANCIAL_TOOLTIPS[key.toLowerCase().replace(/\s+/g, '_')]
  }

  function searchTooltips(query: string): TooltipEntry[] {
    if (!query.trim()) return Object.values(FINANCIAL_TOOLTIPS)
    const q = query.toLowerCase()
    return Object.values(FINANCIAL_TOOLTIPS).filter(
      t =>
        t.term.toLowerCase().includes(q) ||
        t.short.toLowerCase().includes(q) ||
        t.full.toLowerCase().includes(q),
    )
  }

  function getByCategory(category: TooltipEntry['category']): TooltipEntry[] {
    return Object.values(FINANCIAL_TOOLTIPS).filter(t => t.category === category)
  }

  const allTerms = Object.values(FINANCIAL_TOOLTIPS)

  return { getTooltip, searchTooltips, getByCategory, allTerms }
}
